import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { CModal, CModalBody, CModalFooter, CButton,CModalHeader ,CModalTitle} from '@coreui/react';
import ReactHowler from 'react-howler';

const SOUND_SRC = require('../assets/alert.mp3');

// true → cursor-based GET /v1/hot-axles/sound-alert (default; see
// ../axle-temperature-node/docs/hot-axle-sound-alert.md). Flip to false + rebuild to fall
// back to the legacy /v1/hot-axles/count comparison kept below.
const USE_SOUND_ALERT = true;

// localStorage key for the monotonic sound-alert cursor (persisted so a page reload doesn't
// re-sound old alarms).
const LAST_SEEN_KEY = 'hot_axle_sound_last_seen_id';

const HotAxleAlert = () => {
  const [currentHotAxles, setCurrentHotAxles] = useState(0);
  const [showAlert, setShowAlert] = useState(false);
  const [playing, setPlaying] = useState(false);
  const intervalRef = useRef(null);
  const hasInitRef = useRef(false);
  const previousCountRef = useRef(0);

  // New cursor-based path state (coexists with the legacy count refs above).
  const lastSeenIdRef = useRef(0);
  const showAlertRef = useRef(false);   // live mirror of showAlert for the poll closure (B1)
  const retryTimerRef = useRef(null);   // cancellable baseline-retry timer (B3)
  const isMountedRef = useRef(true);

  // ===== Legacy count path (kept as fallback; selected when USE_SOUND_ALERT === false) =====
  const initializeHotAxleCount = async () => {
    if (hasInitRef.current) return;
    try {
      const { data: count } = await axios.get('https://api.irhabd.com/v1/hot-axles/count');
      setCurrentHotAxles(count);
      previousCountRef.current = count;
      hasInitRef.current = true;
    } catch (err) {
      console.error('Error initializing:', err);
      setTimeout(initializeHotAxleCount, 5000);
    }
  };

  const pollHotAxleCount = async () => {
    try {
      const { data: newCount } = await axios.get('https://api.irhabd.com/v1/hot-axles/count');

      if (newCount > previousCountRef.current && !showAlert) {
        setShowAlert(true);
        setPlaying(true);
      }

      setCurrentHotAxles(newCount);
      previousCountRef.current = newCount;

    } catch (err) {
      console.error('Error polling hot axles:', err);
    }
  };

  // ===== New cursor-based sound-alert path (default) =====
  // GET /v1/hot-axles/sound-alert[?sinceId=…] -> { latestEventId, latestTimestamp, latestSeverity }
  const fetchSoundAlert = async (sinceId) => {
    const url = 'https://api.irhabd.com/v1/hot-axles/sound-alert';
    const { data } = await axios.get(sinceId == null ? url : `${url}?sinceId=${sinceId}`);
    return data;
  };

  // Baseline (no sinceId): anchor the cursor to the newest reading without sounding.
  const baselineSoundAlert = async () => {
    try {
      const { latestEventId } = await fetchSoundAlert();
      if (!isMountedRef.current) return;
      lastSeenIdRef.current = latestEventId ?? 0;
      localStorage.setItem(LAST_SEEN_KEY, String(lastSeenIdRef.current));
      hasInitRef.current = true;
    } catch (err) {
      console.error('Error baselining sound-alert:', err);
      // Self-rescheduling retry every 5s; only while mounted so it can't write state after
      // teardown. Keeps retrying until a request succeeds, so it auto-recovers when the
      // connection is restored.
      if (isMountedRef.current) {
        retryTimerRef.current = setTimeout(baselineSoundAlert, 5000);
      }
    }
  };

  // On mount: seed the cursor from localStorage (so a reload doesn't re-sound old alarms),
  // otherwise baseline from the server. Never sounds here.
  const initSoundAlert = () => {
    const stored = parseInt(localStorage.getItem(LAST_SEEN_KEY), 10);
    if (Number.isInteger(stored) && stored >= 0) {
      lastSeenIdRef.current = stored;
      hasInitRef.current = true;
    } else {
      baselineSoundAlert();
    }
  };

  const pollSoundAlert = async () => {
    // Don't compare before a baseline/persisted cursor exists (avoids a false alarm on load).
    if (!hasInitRef.current) return;
    try {
      const res = await fetchSoundAlert(lastSeenIdRef.current);

      if (res.latestEventId === null) {
        // Cursor lost (stale id / sound-config change dropped the anchor) → re-baseline, don't sound.
        await baselineSoundAlert();
        return;
      }

      if (res.latestEventId > lastSeenIdRef.current) {
        lastSeenIdRef.current = res.latestEventId;
        localStorage.setItem(LAST_SEEN_KEY, String(res.latestEventId));
        // Only (re)trigger the sound when the modal isn't already showing.
        if (!showAlertRef.current) {
          setShowAlert(true);
          setPlaying(true);
        }
      }
      // res.latestEventId === lastSeenId → nothing new; do nothing.
    } catch (err) {
      console.error('Error polling sound-alert:', err);
    }
  };

  const handleCloseAlert = () => {
    setPlaying(false);
    setShowAlert(false);
  };

  useEffect(() => {
    if (USE_SOUND_ALERT) {
      initSoundAlert();
      intervalRef.current = setInterval(pollSoundAlert, 60000);
    } else {
      initializeHotAxleCount();
      intervalRef.current = setInterval(pollHotAxleCount, 60000);
    }

    return () => {
      isMountedRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
      }
      setPlaying(false);
    };
  }, []);


  useEffect(() => {
    showAlertRef.current = showAlert;
    if (!showAlert) {
      setPlaying(false);
    }
  }, [showAlert]);


  return (
    <>
      <ReactHowler
        src={SOUND_SRC}
        playing={playing}
        volume={1.0}
        onEnd={() => setPlaying(false)}
      />
      <CModal visible={showAlert}  onClose={handleCloseAlert} >
         <CModalHeader>
                <CModalTitle>Alert!</CModalTitle>
              </CModalHeader>
        <CModalBody>
          <p>  HABD Alert !</p>
        </CModalBody>
        <CModalFooter>
          <CButton onClick={handleCloseAlert} color="primary">
            Okay
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  );
};

export default HotAxleAlert;
